import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Bell, Check, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './NotificationsPanel.css';

import { API as API_URL } from '../utils/apiConfig';

export const NotificationsPanel = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const loadNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/notifications/my`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load notifications:', error);
      }
    }
  }, []);

  // WebSocket connection for real-time notifications
  const connectWebSocket = useCallback(() => {
    if (!user?.id || !isAuthenticated) return;

    // WebSocket disabled — HTTP polling only for stability
    // No raw WS connection (prevents zombie reconnect loops)
  }, [user?.id, isAuthenticated]);

  // HTTP polling for notifications (stable, no WebSocket)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user?.id, loadNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return t('notifications.justNow');
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ride_completed': return '✅';
      case 'dispute_opened': return '⚠️';
      case 'dispute_resolved': return '✔️';
      case 'booking_created': return '🎫';
      case 'booking_confirmed': return '✓';
      case 'payout_approved': return '💰';
      default: return '🔔';
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="notifications-container" ref={panelRef}>
      <button
        className="notification-bell"
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) loadNotifications(); }}
        aria-label={t('notifications.title')}
        data-testid="notifications-bell"
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="notification-badge" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notifications-panel" data-testid="notifications-panel">
          <div className="panel-header">
            <h3>{t('notifications.title')}</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read"
                onClick={markAllRead}
                disabled={loading}
                data-testid="mark-all-read-btn"
              >
                {loading ? t('notifications.markingRead') : t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="notifications-list">
            {notifications.length === 0 ? (
              <div className="no-notifications" data-testid="no-notifications">
                <Bell size={48} style={{ opacity: 0.3 }} />
                <p>{t('notifications.empty')}</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.notification_id || notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => {
                    if (!notification.read) {
                      markAsRead(notification.notification_id || notification.id);
                    }
                    if (notification.link) {
                      window.location.href = notification.link;
                    }
                  }}
                  data-testid={`notification-item-${notification.notification_id || notification.id}`}
                >
                  {!notification.read && <div className="unread-dot" />}
                  <span className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </span>
                  <div className="notification-content">
                    <h4>{notification.title}</h4>
                    <p>{notification.message}</p>
                    <span className="notification-time">
                      {timeAgo(notification.created_at)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
